import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Address } from 'viem';
import { useSignTypedData } from 'wagmi';

// the modules, not the auth barrel: its components import this feature back.
import { useAuthenticatedOwner } from '@/features/auth/authenticatedOwner';
import {
    completeOAuthAccountBindingWithWallet,
    getAccountHashBinding,
} from '@/features/auth/walletAccountBinding';
import type { BootstrapTradingSessionProof } from '@/features/tradingSession/bootstrapStatement';
import {
    readAuthorizationWallet,
    readTradingWallet,
} from '@/features/tradingSession/walletResolution';
import {
    anchorLabel,
    ensureTypedDataChain,
    useAnchorChain,
    useWallet,
    useWalletNetwork,
} from '@/features/wallet';
import {
    provisionEoaAuthorizationWallet,
    requestEoaAuthorizationChallenge,
} from '@/lib/api/authRegistry';
import type {
    OnboardingQueueState,
    OnboardingStatus,
} from '@/lib/api/onboarding';

import {
    completeEoaProvisioning,
    completeSocialProvisioning,
    createDefaultEoaProvisioningDependencies,
    type EoaProvisioningPhase,
} from '../eoaProvisioningCeremony';
import {
    failureFromQueue,
    failureFromThrown,
    stalledProvisioningFailure,
    type ProvisioningFailure,
} from '../services/provisioningFailure';
import {
    detectWalletMismatch,
    type WalletMismatch,
} from '../services/walletMismatch';

/** Which half of the ceremony a failure belongs to, for the checklist. */
export type ProvisioningLeg = 'anchor' | 'hyperevm';

/**
 * A detached creation that neither completes nor reports an error is the exact
 * shape of the incident this hook exists to end. Re-arm the button rather than
 * spin forever; the step promises a few seconds.
 */
const STALL_TIMEOUT_MS = 210_000;

/**
 * Queue states that mean no lane is held any more. Whatever this hook recorded
 * during the last one describes an admission that is gone, so none of it may
 * survive into the next: a `hasStarted` left standing across a rejoin is what
 * disabled the step's button for a run that could never resume.
 */
const LOST_ADMISSION_STATES: readonly OnboardingQueueState[] = [
    'notRedeemed',
    'cancelled',
    'queued',
    'ready',
    'missed',
];

interface UseEoaProvisioningInput {
    /** Posts the HyperEVM statement to `speed/onboarding/wallet`. */
    startTradingWalletCreation: (
        proof: BootstrapTradingSessionProof,
    ) => Promise<void>;
    /**
     * Association run: the social login this account is being created for. The
     * ceremony switches to its cookie-authorized legs; null runs the wallet-only
     * one.
     */
    socialAccountHash?: string | null;
    /** Cookie-authorized HyperEVM creation, for the association run. */
    startSocialWalletCreation?: () => Promise<void>;
    /**
     * The admission queue row. The HyperEVM leg runs detached on the backend, so
     * this is the only channel its outcome ever comes back through.
     */
    queueStatus: OnboardingStatus;
    /**
     * On-chain fact read at auto-open: the owner's authorization wallet exists on
     * the anchor. Lets a reload show the first leg as done without re-signing it.
     * Null when unknown (read failed, or not attempted).
     */
    authorizationWalletExists?: boolean | null;
}

interface LocalFailure {
    failure: ProvisioningFailure;
    leg: ProvisioningLeg | null;
}

function legFromPhase(
    phase: EoaProvisioningPhase | null,
): ProvisioningLeg | null {
    if (!phase) return null;
    return phase === 'anchor-signing' || phase === 'anchor-provisioning'
        ? 'anchor'
        : 'hyperevm';
}

/**
 * Runs the wallet-first provisioning ceremony and exposes its progress, and its
 * failures, for the provision step's checklist. Re-running is safe: legs whose
 * wallet already exists are skipped, and the statements are always signed fresh.
 */
export function useEoaProvisioning({
    startTradingWalletCreation,
    socialAccountHash = null,
    startSocialWalletCreation,
    queueStatus,
    authorizationWalletExists = null,
}: UseEoaProvisioningInput) {
    const { signTypedDataAsync } = useSignTypedData();
    const { ensureAnchor } = useWalletNetwork();
    const { anchorChainId } = useAnchorChain();
    const authenticatedOwner = useAuthenticatedOwner();
    const { address: connectedAddress } = useWallet();
    const [phase, setPhase] = useState<EoaProvisioningPhase | null>(null);
    // Failures this session produced: a throw out of the ceremony, or a
    // detached run that went quiet. Queue-reported ones are derived below.
    const [localFailure, setLocalFailure] = useState<LocalFailure | null>(null);
    const [isRunning, setIsRunning] = useState(false);
    const [hasStarted, setHasStarted] = useState(false);
    const [authorizationWallet, setAuthorizationWallet] =
        useState<Address | null>(null);
    const [tradingWallet, setTradingWallet] = useState<Address | null>(null);
    // Set by the preflight when it refuses to run. The panel itself is derived
    // from the live pair below, so switching the wallet back to the owner
    // clears it without anyone having to notice and reset a flag.
    const [mismatchBlocked, setMismatchBlocked] = useState(false);

    // memoised on the pair: consumers key effects on this object, and it is
    // recomputed on every parent render (queue polling, balance refetch), which
    // would otherwise cost them a setState and a render each time.
    const liveMismatch = useMemo(
        () => detectWalletMismatch(authenticatedOwner, connectedAddress),
        [authenticatedOwner, connectedAddress],
    );
    const mismatch: WalletMismatch | null = mismatchBlocked
        ? liveMismatch
        : null;
    const hasLiveMismatch = !!liveMismatch;
    useEffect(() => {
        if (!hasLiveMismatch) setMismatchBlocked(false);
    }, [hasLiveMismatch]);

    const queueFailure = failureFromQueue(queueStatus);
    // A lost admission outranks anything local: rejoining is the only action
    // that can move the user forward, whatever happened before it.
    const failure =
        queueFailure?.kind === 'rejoin'
            ? queueFailure
            : (localFailure?.failure ?? queueFailure);
    const failedLeg =
        failure === localFailure?.failure
            ? localFailure.leg
            : failure && failure.kind === 'retry'
                ? 'hyperevm'
                : null;
    // The stall failure is local, so it also releases the disabled button.
    const detachedProvisioning =
        queueStatus.state === 'provisioning' && !localFailure;

    const reset = useCallback(() => {
        setPhase(null);
        setHasStarted(false);
        setLocalFailure(null);
        setMismatchBlocked(false);
    }, []);

    const run = useCallback(
        async (owner: string) => {
            // Preflight. Every leg below signs as `owner`, the account the
            // wallet is live on, while the app token these statements are
            // posted under belongs to whoever signed in. Running them on a
            // mismatch can only end in `ownerAuthorizationRequired`, after the
            // user has approved signatures for nothing, so it stops here.
            const blocking = detectWalletMismatch(authenticatedOwner, owner);
            if (blocking) {
                setMismatchBlocked(true);
                setLocalFailure(null);
                setPhase(null);
                return;
            }
            setMismatchBlocked(false);
            setLocalFailure(null);
            setIsRunning(true);
            setHasStarted(true);
            let reached: EoaProvisioningPhase | null = null;
            const trackPhase = (next: EoaProvisioningPhase) => {
                reached = next;
                setPhase(next);
            };
            try {
                const result = socialAccountHash
                    ? await completeSocialProvisioning(
                        owner,
                        socialAccountHash,
                        {
                            anchorChainId,
                            getAuthorizationWallet: async (walletOwner) =>
                                readAuthorizationWallet(
                                    walletOwner,
                                    anchorChainId,
                                ),
                            getTradingWallet: readTradingWallet,
                            getAccountHashBinding: async (accountHash) =>
                                getAccountHashBinding(
                                    accountHash,
                                    anchorChainId,
                                ),
                            bindAccountHash: async (
                                bindOwner,
                                accountHash,
                            ) => {
                                // one leg signs typed data pinned to the anchor,
                                // the other sends a transaction there; the
                                // binding module never switches the network
                                // itself.
                                if (!(await ensureAnchor())) {
                                    throw new Error(
                                        `Switch your connected wallet to ${anchorLabel(anchorChainId)}.`,
                                    );
                                }
                                return completeOAuthAccountBindingWithWallet(
                                    bindOwner,
                                    accountHash,
                                    undefined,
                                    anchorChainId,
                                );
                            },
                            startTradingWalletCreation: async () => {
                                if (!startSocialWalletCreation) {
                                    throw new Error(
                                        'Snappy Wallet setup is unavailable right now. Please try again.',
                                    );
                                }
                                await startSocialWalletCreation();
                            },
                        },
                        trackPhase,
                    )
                    : await completeEoaProvisioning(
                        owner,
                        createDefaultEoaProvisioningDependencies({
                            anchorChainId,
                            getAuthorizationWallet: async (walletOwner) =>
                                readAuthorizationWallet(
                                    walletOwner,
                                    anchorChainId,
                                ),
                            getTradingWallet: readTradingWallet,
                            requestChallenge: async (challengeOwner) =>
                                requestEoaAuthorizationChallenge(
                                    challengeOwner,
                                    anchorChainId,
                                ),
                            signOwnerBinding: async (typedData) => {
                                await ensureTypedDataChain(typedData);
                                return signTypedDataAsync(
                                    typedData as Parameters<
                                        typeof signTypedDataAsync
                                    >[0],
                                );
                            },
                            signBootstrapStatement: async (typedData) => {
                                await ensureTypedDataChain(typedData);
                                return signTypedDataAsync(
                                    typedData as Parameters<
                                        typeof signTypedDataAsync
                                    >[0],
                                );
                            },
                            provisionAuthorizationWallet: async (provisionInput) =>
                                provisionEoaAuthorizationWallet({
                                    ...provisionInput,
                                    anchorChainId,
                                }),
                            startTradingWalletCreation,
                        }),
                        trackPhase,
                    );
                setAuthorizationWallet(result.authorizationWallet);
                // an idempotent re-run can find the second wallet already
                // on-chain, which finishes the checklist without any queue
                // transition to report it.
                if (result.tradingWallet)
                    setTradingWallet(result.tradingWallet);
                // from here the queue status owns the outcome, so nothing may
                // stay pinned to a phase the ceremony has already left.
                if (!result.tradingWalletCreationStarted) setPhase(null);
            } catch (caught) {
                // the pinned phase used to paint the error onto whichever row
                // happened to be active; attribute it to the leg that was
                // actually running, and stop pinning.
                setPhase(null);
                setLocalFailure({
                    failure: failureFromThrown(caught),
                    leg: legFromPhase(reached),
                });
            } finally {
                setIsRunning(false);
            }
        },
        [
            anchorChainId,
            authenticatedOwner,
            ensureAnchor,
            signTypedDataAsync,
            socialAccountHash,
            startSocialWalletCreation,
            startTradingWalletCreation,
        ],
    );

    // A queue-reported outcome ends the ceremony's own progress display.
    const settled = queueStatus.state === 'completed' || !!queueFailure;
    useEffect(() => {
        if (settled) setPhase(null);
    }, [settled]);

    // The lane is gone, however the user got there: it expired, they rejoined
    // from the queue step, or the voucher was cancelled. The ceremony that ran
    // under it is over, so it stops claiming to be in flight. Without this a
    // rejoin came back to a step whose button was disabled and labelled
    // "Setting up your account…" with nothing running behind it, and stayed
    // that way until the fresh lane expired too.
    const admissionLost =
        queueFailure?.kind === 'rejoin' ||
        LOST_ADMISSION_STATES.includes(queueStatus.state);
    useEffect(() => {
        if (admissionLost) reset();
    }, [admissionLost, reset]);

    // Nothing is running, and nothing is going to report back: either the
    // detached run went quiet, or a re-admitted lane left a finished local run
    // with no outcome to wait for. Re-arm rather than spin (or sit disabled)
    // until the lane expires.
    const hasFailure = !!failure;
    const isBlockedByMismatch = !!mismatch;
    const awaitingOutcome =
        !isRunning &&
        !hasFailure &&
        !isBlockedByMismatch &&
        (detachedProvisioning ||
            (queueStatus.state === 'admitted' && hasStarted));
    useEffect(() => {
        if (!awaitingOutcome) return;
        const timer = window.setTimeout(() => {
            setLocalFailure({
                failure: stalledProvisioningFailure(),
                leg: 'hyperevm',
            });
        }, STALL_TIMEOUT_MS);
        return () => window.clearTimeout(timer);
    }, [awaitingOutcome]);

    return {
        phase,
        anchorChainId,
        failure,
        failedLeg,
        /** The wallet points at another account than the login: nothing ran. */
        mismatch,
        isRunning,
        hasStarted,
        detachedProvisioning,
        authorizationWallet,
        // reaching the HyperEVM leg means the anchor one landed, which is what
        // keeps the checklist honest when the second leg then fails.
        authorizationWalletReady:
            !!authorizationWallet ||
            authorizationWalletExists === true ||
            phase === 'hyperevm-signing' ||
            phase === 'hyperevm-provisioning',
        tradingWalletReady: !!tradingWallet,
        run,
        reset,
    };
}
